/**
 * Close-out transform for terminal task events (harmoniq-0-5-68 → 0-5-81).
 *
 * Problem this fixes
 * ──────────────────
 * When the mother-agent emits a task-level terminal event (``done``,
 * ``cancelled``, ``error``), the UI has to close any progress cards
 * that are still in ``running`` state — those are cards whose final
 * step's ``Done`` substatus event never arrived, or arrived out of
 * order, or were in-flight when the user hit Cancel.
 *
 * Pre-fix (harmoniq-0-5-68): the closer ran ONLY on the ``done``
 * event and always flipped status to ``"done"``. That left two
 * gaps:
 *   • Cancel / error events didn't close running cards at all →
 *     the in-flight card stayed ``running`` forever. Its substep's
 *     ``done_at`` was never stamped, ``useTaskTimings.anyRunning``
 *     stayed true, ``runEndedAt`` stayed null, the elapsed-time
 *     label kept ticking, and the phase's Gantt segment kept the
 *     striped ``tps-seg--running`` animation — user report
 *     "timer keeps ticking even after cancel" (2026-04-24).
 *   • On ``done`` we marked closed cards as ``"done"`` — but a
 *     card that happened to be running when the run failed /
 *     cancelled shouldn't show a green check afterwards; it
 *     should show an X. The green check is a successful step;
 *     the X is an interrupted one.
 *
 * What this function does
 * ───────────────────────
 * For every message with a ``progressCard`` whose status is
 * ``"running"``:
 *   1. Flip card status to the caller-supplied ``terminalStatus``
 *      (default ``"done"`` for backward-compat with the existing
 *      ``done``-event handler).
 *   2. Stamp ``started_at`` / ``completed_at`` (preserve existing;
 *      only seed from ``closeTs`` when missing — prefer real data).
 *   3. Walk substeps[] and close any running ones with the same
 *      terminal status, preserving existing endpoints.
 *
 * For substeps that are ALREADY terminal (done / failed / skipped /
 * cancelled) but missing endpoints, we rescue them with ``closeTs``
 * regardless of the new ``terminalStatus`` — the rescue is about
 * endpoints, not status (their status is already correct from the
 * agent).
 *
 * Pure function — does not mutate input, returns a new array.
 * Extracted from ``Workspace.jsx``'s terminal-event handlers so
 * the transform is unit-testable without standing up the React /
 * WebSocket harness.
 *
 * @param {Array}  messages — current messages array
 * @param {number} closeTs — ms-since-epoch to use when a timestamp
 *   is missing. Typically the terminal event's own timestamp.
 * @param {object} [options]
 * @param {"done"|"cancelled"|"failed"} [options.terminalStatus="done"]
 *   — the status to flip running cards (and their running substeps)
 *   into. Callers pass ``"cancelled"`` on the cancel-event handler
 *   and ``"failed"`` on the error-event handler so the UI can
 *   render the right status icon + bar-segment colour.
 * @returns {Array} — new messages array with running cards closed
 */
export function closeRunningProgressCards(messages, closeTs, options = {}) {
  if (!Array.isArray(messages)) return messages;
  const terminalStatus = options.terminalStatus || "done";

  return messages.map((m) => {
    if (m?.role !== "aria" || m?.progressCard?.status !== "running") {
      return m;
    }
    const pc = m.progressCard;
    const closedSubsteps = Array.isArray(pc.substeps)
      ? pc.substeps.map((s) => {
          // Running substeps → flip to the caller's terminal status
          // and stamp any missing endpoints. When the parent run
          // was cancelled/failed, the in-flight substep deserves
          // the same terminal marker as the parent card — not
          // "done", which would misrepresent an interrupted step
          // as successfully completed.
          if (s?.status === "running") {
            return {
              ...s,
              status: terminalStatus,
              // Preserve any existing timestamps — we only seed from
              // closeTs when the field is genuinely missing so we
              // never stomp a real value from the accumulator.
              running_at: s.running_at ?? closeTs,
              done_at: s.done_at ?? closeTs,
            };
          }
          // Already-done substeps (harmoniq-0-5-71 bugfix): rescue
          // any that landed in a terminal state WITHOUT endpoints.
          // Extending "terminal" to include the new "cancelled"
          // value we might flip TO — otherwise a cancel-closer's
          // output would have cancelled substeps still missing
          // endpoints on subsequent passes. This check is about
          // TIMING-FIELD rescue only; we never change a substep's
          // status here (the agent emitted it, we trust it).
          const terminal =
            s?.status === "done" ||
            s?.status === "failed" ||
            s?.status === "error" ||
            s?.status === "skipped" ||
            s?.status === "cancelled";
          if (terminal && (s.running_at == null || s.done_at == null)) {
            return {
              ...s,
              running_at: s.running_at ?? closeTs,
              done_at: s.done_at ?? closeTs,
            };
          }
          return s;
        })
      : undefined;

    return {
      ...m,
      progressCard: {
        ...pc,
        status: terminalStatus,
        started_at: pc.started_at ?? closeTs,
        completed_at: pc.completed_at ?? closeTs,
        ...(closedSubsteps ? { substeps: closedSubsteps } : {}),
      },
    };
  });
}
